import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";

const log = createFileLogger('ENTITY_FIND');

export const entityFindCommand: SelfContainedCommand = {
  id: 'entity_find',
  description: `Find entities using fuzzy text search or exact filters.

**SUPPORTED ENTITIES:**
- All entity types (Documents, Notes, Sheets, Canvas, Presentations, Folders, Files)

**Search Modes:**
1. **Fuzzy Search** (recommended for user-provided names):
   - Use searchQuery parameter for partial, case-insensitive matching
   - Example: searchQuery="q4" finds "Q4 2024 Reports", "Q4 Planning", etc.
   - Example: searchQuery="mixpanel" finds "Mixpanel Analytics API", "Mixpanel Integration"

2. **Exact Filters** (for structured queries):
   - Use filters parameter for precise key-value matching
   - Example: filters={"skeleton.state": "CA", "skeleton.status": "active"}

**Use Cases:**
- Find folders by name (even with typos or partial names)
- **⭐ Find APIConnections** - Search for API integrations by service name (e.g., "mixpanel", "stripe", "github")
- Find all entities of a specific type
- Discover existing entities before creating duplicates
- Search by skeleton properties

**Best Practices:**
- ALWAYS use searchQuery for user-provided folder/entity names
- ⭐ **For API workflows:** Search for APIConnections before asking user for credentials
  - Example: searchQuery="mixpanel" entityType="APIConnection"
  - APIConnections are org-level shared resources accessible from any space
  - Then read the entity to get credentials
- Use filters for structured metadata (status, type, etc.)
- Combine both: searchQuery + entityType for focused results`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'searchQuery',
        type: 'string',
        description: 'Fuzzy text search across entity name and content. Use this when the user provides partial or approximate names (e.g., "q4" finds "Q4 2024 Reports"). Case-insensitive partial matching.',
        required: false,
        examples: [
          'research',      // Finds "Research Papers", "Clinical Research", etc.
          'q4',            // Finds "Q4 2024", "Q4 Reports", etc.
          'client',        // Finds "Client Projects", "ABC Client", etc.
        ]
      },
      {
        name: 'entityType',
        type: 'string',
        description: 'The type of entity to find (e.g., "Vehicle", "Account", "Task", "Document", "Group")',
        required: false
      },
      {
        name: 'filters',
        type: 'object',
        description: 'Filter criteria as key-value pairs for exact matches (e.g., {"skeleton.make": "Ford", "skeleton.state": "CA"})',
        required: false
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum number of results to return',
        required: false,
        defaultValue: 50
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { searchQuery: 'q4', entityType: 'Group' },
        expectedOutput: createSuccessResult('Found entities matching "q4"', {
          entities: [
            { _id: 'group_123', name: 'Q4 2024 Reports', entityType: 'Group' },
            { _id: 'group_456', name: 'Q4 Planning', entityType: 'Group' }
          ],
          count: 2
        }),
        description: 'Fuzzy search for folders with "q4" in name',
        context: 'When user says "create doc in q4 folder"'
      },
      {
        input: { searchQuery: 'research', entityType: 'Group' },
        expectedOutput: createSuccessResult('Found entities matching "research"', {
          entities: [
            { _id: 'group_789', name: 'Research Papers', entityType: 'Group' }
          ],
          count: 1
        }),
        description: 'Fuzzy search for folders containing "research"',
        context: 'When user provides partial folder name'
      },
      {
        input: { entityType: 'Vehicle' },
        expectedOutput: createSuccessResult('Found entities', {
          entities: [
            { _id: 'vehicle_123', name: 'Ford F-250', entityType: 'Vehicle' }
          ],
          count: 1
        }),
        description: 'Find all vehicles',
        context: 'When listing all entities of a type'
      },
      {
        input: { 
          entityType: 'Account', 
          filters: { 'skeleton.serviceType': 'dmv', 'skeleton.state': 'CA' }
        },
        expectedOutput: createSuccessResult('Found entities', {
          entities: [
            { _id: 'account_123', name: 'CA DMV', entityType: 'Account' }
          ],
          count: 1
        }),
        description: 'Exact filter for CA DMV account',
        context: 'When searching by structured metadata'
      }
    ],
    bad: [
      {
        input: {},
        error: 'Must provide either searchQuery, entityType, or filters',
        why: 'At least one search criterion is required',
        fix: 'Provide searchQuery for fuzzy search or entityType/filters for structured search'
      }
    ]
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { searchQuery, entityType, filters = {}, limit = 50 } = params;
    
    try {
      // Graceful default: If no criteria, return root-level entities
      const hasFilters = filters && Object.keys(filters).length > 0;
      if (!searchQuery && !entityType && !hasFilters) {
        log.info('[entity_find] No criteria provided, listing root entities as default');
        
        const entityService = (await import('../../services/entityService')).entityService;
        const allEntities = await entityService.queryEntities({});
        const allFolders = allEntities.filter((e: any) => e.entityType === 'Group');
        const allChildIds = new Set(
          allFolders.flatMap((f: any) => f.skeleton?.childEntities || [])
        );
        
        const rootEntities = allEntities
          .filter((e: any) =>
            !allChildIds.has(e._id) &&
            e.entityType !== 'DocVersion' &&
            e.entityType !== 'Message'
          )
          .slice(0, limit);
        
        return createSuccessResult(
          `No search criteria provided - listing ${rootEntities.length} root-level entities`,
          {
            entities: rootEntities,
            count: rootEntities.length,
            note: 'Showing root-level entities (not in folders). Use searchQuery, entityType, or filters to narrow results.'
          },
          {
            format: 'json',
            metadata: {
              executionTime: Date.now() - startTime,
              method: 'default_root_listing'
            } as any
          }
        );
      }
      
      console.log('[entity_find] Finding entities:', JSON.stringify({ 
        searchQuery, 
        entityType, 
        filters, 
        limit 
      }, null, 2));
      
      let entities;
      
      // ✅ CLIENT-SIDE SEARCH: Search through EntityStore (backend endpoint is deprecated)
      // The backend /api/entities/search endpoint was deprecated due to performance issues
      const entityStore = useEntityStore.getState();
      
      console.log('[entity_find] 🔍 EntityStore state:', JSON.stringify({
        totalEntities: entityStore.entities.length,
        currentSpaceId: entityStore.currentSpaceId,
        searchQuery: searchQuery,
        entityType: entityType,
        hasFilters: Object.keys(filters).length > 0
      }, null, 2));
      
      // ✅ Start with all entities (including org-level shared resources)
      // Filter out only system types that should never be shown
      let allEntities = entityStore.entities.filter((e: any) => {
        const isSystemType = ['DocVersion', 'Message', 'Space'].includes(e.entityType);
        const isHidden = e.trashed === true || e.hidden === true;
        
        // Include:
        // 1. Entities in current space
        // 2. Org-level shared resources (APIConnection, Integration) with spaceId=null
        const belongsToCurrentSpace = e.spaceId === entityStore.currentSpaceId;
        const isSharedResource = (
          (e.entityType === 'APIConnection' || e.entityType === 'Integration') &&
          (e.spaceId === null || e.spaceId === undefined)
        );
        
        return (belongsToCurrentSpace || isSharedResource) && !isSystemType && !isHidden;
      });
      
      console.log('[entity_find] 📊 Searchable entities (including org-level shared resources):', JSON.stringify({
        count: allEntities.length,
        entityTypes: Array.from(new Set(allEntities.map((e: any) => e.entityType))),
        apiConnectionCount: allEntities.filter(e => e.entityType === 'APIConnection').length,
        integrationCount: allEntities.filter(e => e.entityType === 'Integration').length,
        sharedResourcesCount: allEntities.filter(e => 
          (e.entityType === 'APIConnection' || e.entityType === 'Integration') &&
          (e.spaceId === null || e.spaceId === undefined)
        ).length
      }, null, 2));
      
      // FUZZY SEARCH: Filter by search query (case-insensitive partial matching)
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        
        // Normalized query for fuzzy matching (remove spaces and special chars)
        const normalizedQuery = query.replace(/[\s-_]/g, '');
        
        allEntities = allEntities.filter((e: any) => {
          const name = (e.name || '').toLowerCase();
          const llmId = (e.llmId || '').toLowerCase();
          
          // Normalized versions for fuzzy matching
          const normalizedName = name.replace(/[\s-_]/g, '');
          const normalizedLlmId = llmId.replace(/[\s-_]/g, '');
          
          // For APIConnection entities, also search skeleton fields
          if (e.entityType === 'APIConnection' && e.skeleton) {
            const serviceName = (e.skeleton.serviceName || '').toLowerCase();
            const managedApiId = (e.skeleton.managedApiId || '').toLowerCase();
            const displayName = (e.skeleton.displayName || '').toLowerCase();
            
            // Normalized skeleton fields
            const normalizedServiceName = serviceName.replace(/[\s-_]/g, '');
            const normalizedManagedApiId = managedApiId.replace(/[\s-_]/g, '');
            const normalizedDisplayName = displayName.replace(/[\s-_]/g, '');
            
            // Try both exact and normalized matching
            return name.includes(query) || 
                   llmId.includes(query) || 
                   serviceName.includes(query) || 
                   managedApiId.includes(query) || 
                   displayName.includes(query) ||
                   normalizedName.includes(normalizedQuery) ||
                   normalizedLlmId.includes(normalizedQuery) ||
                   normalizedServiceName.includes(normalizedQuery) ||
                   normalizedManagedApiId.includes(normalizedQuery) ||
                   normalizedDisplayName.includes(normalizedQuery);
          }
          
          // For other entities, try both exact and normalized matching
          return name.includes(query) || 
                 llmId.includes(query) ||
                 normalizedName.includes(normalizedQuery) ||
                 normalizedLlmId.includes(normalizedQuery);
        });
        
        console.log('[entity_find] 🔍 After searchQuery filter:', JSON.stringify({
          query: searchQuery,
          remainingCount: allEntities.length
        }, null, 2));
      }
      
      // Filter by entity type if specified
      if (entityType) {
        allEntities = allEntities.filter((e: any) => e.entityType === entityType);
        
        console.log('[entity_find] 🔍 After entityType filter:', JSON.stringify({
          entityType: entityType,
          remainingCount: allEntities.length
        }, null, 2));
      }
      
      // Apply additional filters if specified
      if (Object.keys(filters).length > 0) {
        allEntities = allEntities.filter((e: any) => {
          return Object.entries(filters).every(([key, value]) => {
            // Support nested properties (e.g., "skeleton.status")
            const keys = key.split('.');
            let current: any = e;
            for (const k of keys) {
              if (!current || typeof current !== 'object') return false;
              current = current[k];
            }
            return current === value;
          });
        });
        
        console.log('[entity_find] 🔍 After filters:', JSON.stringify({
          filters: filters,
          remainingCount: allEntities.length
        }, null, 2));
      }
      
      // Apply limit to local results
      entities = allEntities.slice(0, limit);

      // ─── Backend search augmentation ────────────────────────────────────────
      // For queries >= 4 chars, also call the backend $text search to surface
      // content matches (skeleton notes, tags, etc.) not visible from names alone.
      // Merge with local results, deduplicating by _id, backend results ranked first.
      if (searchQuery && searchQuery.trim().length >= 4) {
        try {
          const { searchUniversal } = await import('../../services/searchService');
          const response = await searchUniversal({
            query: searchQuery.trim(),
            mode: 'navigational',
            entityTypes: entityType ? [entityType] : undefined,
            limit,
          });

          if (response.results.length > 0) {
            const localEntityMap = new Map(entityStore.entities.map((e: any) => [e._id, e]));
            const resolvedBackend: any[] = [];
            for (const r of response.results) {
              const full = localEntityMap.get(r.entity_id);
              if (full) resolvedBackend.push(full);
            }

            const seenIds = new Set(resolvedBackend.map((e: any) => e._id));
            const localOnly = entities.filter((e: any) => !seenIds.has(e._id));
            entities = [...resolvedBackend, ...localOnly].slice(0, limit);

            log.info('[entity_find] universal search augmented results', {
              query: searchQuery,
              backendCount: resolvedBackend.length,
              localCount: localOnly.length,
              totalReturned: entities.length,
            });
          }
        } catch (backendErr) {
          log.warn('[entity_find] backend search failed, using local-only results', { error: backendErr });
        }
      }
      // ─────────────────────────────────────────────────────────────────────────
      
      console.log('[entity_find] ✅ Final results:', JSON.stringify({
        totalMatches: allEntities.length,
        returned: entities.length,
        limit: limit,
        entities: entities.map((e: any) => ({
          _id: e._id,
          llmId: e.llmId,
          name: e.name,
          entityType: e.entityType
        }))
      }, null, 2));
      
      return createSuccessResult(
        searchQuery 
          ? `Found ${entities.length} entities matching "${searchQuery}"`
          : `Found ${entities.length} ${entityType || 'entities'}`,
        {
          entities: entities,
          count: entities.length,
          searchQuery: searchQuery || null,
          entityType: entityType || null
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: searchQuery ? 'backend_augmented_search' : 'exact_query',
            searchType: searchQuery ? 'hybrid' : 'exact'
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[entity_find] Error:', error);
      return createErrorResult(
        'Entity search failed',
        `Failed to find entities: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { searchQuery, entityType, filters },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

